import React, { useState, useEffect, useMemo } from 'react';
import { Settings as SettingsIcon, User, Shield, Database, HardDrive, Check, X, Loader2, ExternalLink, History, Clock, CloudUpload, AlertTriangle, RefreshCw, Building2, Upload, Save, FileText, Lock, Eye, EyeOff, Zap, CheckCircle2, Sun, Moon, Plus, Edit, Trash2, Package, Globe, Mail, Link2, Unlink, KeyRound } from 'lucide-react';
import EmailPreview from '../components/EmailPreview';
import HtmlEditor from '../components/HtmlEditor';
import BackupPanel from '../components/BackupPanel';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { getRoleLabel } from '../lib/utils';
import { adminAPI, companySettingsAPI, authAPI } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Settings = () => {
  const { user, hasRole } = useAuth();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    address: '',
    address_line1: '',
    address_line2: '',
    vat_number: '',
    email: '',
    phone: '',
    default_vat_percentage: 6,
    default_payment_terms: '',
    default_validity_days: 8,
    default_cleaning_price_per_panel: 12.50,
    default_travel_costs: 30.49,
    logo_url: null,
    offerte_number_prefix: '',
    installation_offerte_prefix: '',
    included_in_offerte: '',
    email_signature: '',
    // Installation payment terms
    installation_term1_percentage: 40,
    installation_term1_description: 'Voorschot bij bestelling',
    installation_term2_percentage: 50,
    installation_term2_description: 'Na montage en inwerkingstelling',
    installation_term3_percentage: 10,
    installation_term3_description: 'Na AREI-keuring',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Restore state
  const [restoreOptions, setRestoreOptions] = useState([]);
  const [restoreLoading, setRestoreLoading] = useState(true);
  const [selectedRestore, setSelectedRestore] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    items: [],
    vat_percentage: 6,
    payment_terms: '',
    included_in_offerte: '',
    notes: ''
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);

  // Email integration state
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailAccountsLoading, setEmailAccountsLoading] = useState(true);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogType, setEmailDialogType] = useState('personal'); // 'personal' or 'company'
  const [emailForm, setEmailForm] = useState({
    email_address: '',
    imap_password: '',
    display_name: '',
    account_type: 'personal'
  });
  const [savingEmailAccount, setSavingEmailAccount] = useState(false);
  const [testingEmailAccount, setTestingEmailAccount] = useState(null);
  const [syncingEmailAccount, setSyncingEmailAccount] = useState(null);
  const [deleteEmailAccountId, setDeleteEmailAccountId] = useState(null);
  const [updatePasswordAccountId, setUpdatePasswordAccountId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Computed email accounts (split by type)
  const personalEmailAccount = useMemo(() => {
    return emailAccounts.find(acc => acc.account_type === 'personal' && acc.user_id === user?.id);
  }, [emailAccounts, user]);
  
  const companyEmailAccounts = useMemo(() => {
    return emailAccounts.filter(acc => acc.account_type === 'company' || !acc.account_type);
  }, [emailAccounts]);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(true);
  const [emailTemplateDialogOpen, setEmailTemplateDialogOpen] = useState(false);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState(null);
  const [emailTemplateForm, setEmailTemplateForm] = useState({
    template_type: 'reiniging_offerte',
    name: '',
    subject: '',
    body_html: '',
    is_active: true,
    cc_emails: '',
    cc_creator: false
  });
  const [savingEmailTemplate, setSavingEmailTemplate] = useState(false);
  const [deleteEmailTemplateId, setDeleteEmailTemplateId] = useState(null);

  // Fetch email accounts
  const fetchEmailAccounts = async () => {
    try {
      const res = await api.get('/email-accounts');
      setEmailAccounts(res.data);
    } catch (error) {
      console.error('Error fetching email accounts:', error);
    } finally {
      setEmailAccountsLoading(false);
    }
  };

  // Add email account
  const handleAddEmailAccount = async () => {
    if (!emailForm.email_address || !emailForm.imap_password) {
      toast.error('Vul email adres en wachtwoord in');
      return;
    }

    setSavingEmailAccount(true);
    try {
      await api.post('/email-accounts', {
        email_address: emailForm.email_address,
        imap_password: emailForm.imap_password,
        display_name: emailForm.display_name || emailForm.email_address,
        account_type: emailDialogType
      });
      toast.success(`${emailDialogType === 'personal' ? 'Persoonlijk' : 'Bedrijfs'} email account toegevoegd!`);
      setEmailDialogOpen(false);
      setEmailForm({ email_address: '', imap_password: '', display_name: '', account_type: 'personal' });
      fetchEmailAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon email account niet toevoegen');
    } finally {
      setSavingEmailAccount(false);
    }
  };

  // Test email account connection
  const handleTestEmailAccount = async (accountId) => {
    setTestingEmailAccount(accountId);
    try {
      const res = await api.post(`/email-accounts/${accountId}/test`);
      if (res.data.success) {
        toast.success(res.data.message || 'Verbinding succesvol!');
      } else {
        toast.error(res.data.error || 'Verbinding mislukt');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test mislukt');
    } finally {
      setTestingEmailAccount(null);
    }
  };

  // Sync email account
  const handleSyncEmailAccount = async (accountId) => {
    setSyncingEmailAccount(accountId);
    try {
      const res = await api.post(`/email-accounts/${accountId}/sync`);
      toast.success(`${res.data.emails_matched || 0} nieuwe emails gesynchroniseerd`);
      fetchEmailAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Synchronisatie mislukt');
    } finally {
      setSyncingEmailAccount(null);
    }
  };

  // Delete email account
  const handleDeleteEmailAccount = async () => {
    if (!deleteEmailAccountId) return;

    try {
      await api.delete(`/email-accounts/${deleteEmailAccountId}`);
      toast.success('Email account verwijderd');
      setDeleteEmailAccountId(null);
      fetchEmailAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon niet verwijderen');
    }
  };

  // Update email account password
  const handleUpdatePassword = async () => {
    if (!updatePasswordAccountId || !newPassword) return;

    try {
      await api.put(`/email-accounts/${updatePasswordAccountId}`, {
        imap_password: newPassword
      });
      toast.success('Wachtwoord bijgewerkt');
      setUpdatePasswordAccountId(null);
      setNewPassword('');
      fetchEmailAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon wachtwoord niet bijwerken');
    }
  };

  // Fetch email templates
  const fetchEmailTemplates = async () => {
    try {
      const res = await api.get('/email-templates');
      setEmailTemplates(res.data);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    } finally {
      setEmailTemplatesLoading(false);
    }
  };

  // Save email template
  const handleSaveEmailTemplate = async () => {
    if (!emailTemplateForm.name || !emailTemplateForm.subject) {
      toast.error('Vul naam en onderwerp in');
      return;
    }

    setSavingEmailTemplate(true);
    try {
      const payload = {
        ...emailTemplateForm,
        cc_emails: (emailTemplateForm.cc_emails || '').split(',').map(s => s.trim()).filter(s => s.includes('@')),
        cc_creator: !!emailTemplateForm.cc_creator
      };
      if (editingEmailTemplate) {
        await api.put(`/email-templates/${editingEmailTemplate.id}`, payload);
        toast.success('Email template bijgewerkt');
      } else {
        await api.post('/email-templates', payload);
        toast.success('Email template aangemaakt');
      }
      setEmailTemplateDialogOpen(false);
      setEditingEmailTemplate(null);
      setEmailTemplateForm({
        template_type: 'reiniging_offerte',
        name: '',
        subject: '',
        body_html: '',
        is_active: true,
        cc_emails: '',
        cc_creator: false
      });
      fetchEmailTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon template niet opslaan');
    } finally {
      setSavingEmailTemplate(false);
    }
  };

  // Delete email template
  const handleDeleteEmailTemplate = async () => {
    if (!deleteEmailTemplateId) return;

    try {
      await api.delete(`/email-templates/${deleteEmailTemplateId}`);
      toast.success('Email template verwijderd');
      setDeleteEmailTemplateId(null);
      fetchEmailTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon niet verwijderen');
    }
  };

  // Helper function to unescape HTML entities that might be double-escaped
  const unescapeHtml = (html) => {
    if (!html) return '';
    // Unescape common HTML entities that might be stored escaped in database
    return html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  };

  // Open edit email template dialog
  const handleEditEmailTemplate = (template) => {
    setEditingEmailTemplate(template);
    setEmailTemplateForm({
      template_type: template.template_type,
      name: template.name,
      subject: template.subject,
      body_html: unescapeHtml(template.body_html),
      is_active: template.is_active,
      cc_emails: (template.cc_emails || []).join(', '),
      cc_creator: !!template.cc_creator
    });
    setEmailTemplateDialogOpen(true);
  };

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const res = await api.get('/installation-templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!templateForm.name) {
      toast.error('Geef een naam op');
      return;
    }
    
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await api.put(`/installation-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template bijgewerkt');
      } else {
        await api.post('/installation-templates', templateForm);
        toast.success('Template aangemaakt');
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Kon template niet opslaan');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async () => {
    if (!deleteTemplateId) return;
    
    try {
      await api.delete(`/installation-templates/${deleteTemplateId}`);
      toast.success('Template verwijderd');
      setDeleteTemplateId(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Kon template niet verwijderen');
    }
  };

  // Open edit template dialog
  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      items: template.items || [],
      vat_percentage: template.vat_percentage || 6,
      payment_terms: template.payment_terms || '',
      included_in_offerte: template.included_in_offerte || '',
      notes: template.notes || ''
    });
    setTemplateDialogOpen(true);
  };

  // Open new template dialog
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      description: '',
      items: [],
      vat_percentage: 6,
      payment_terms: '40% Voorschot\n50% Na montage van de installatie en inwerkingstelling\n10% Na AREI-keuring op conformiteit door een erkend organisme',
      included_in_offerte: 'Leveren en plaatsen van de installatie\nTransport en materiaalkosten\nAREI-keuring op conformiteit door een erkend organisme',
      notes: ''
    });
    setTemplateDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Vul alle velden in');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Nieuwe wachtwoorden komen niet overeen');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Nieuw wachtwoord moet minimaal 6 tekens bevatten');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Wachtwoord succesvol gewijzigd');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon wachtwoord niet wijzigen');
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    fetchRestoreOptions();
    fetchCompanySettings();
    fetchTemplates();
    fetchEmailAccounts();
    fetchEmailTemplates();
  }, []);
  
  const fetchCompanySettings = async () => {
    try {
      const response = await companySettingsAPI.get();
      // Merge with defaults to ensure all fields exist (even if not in DB yet)
      setCompanySettings(prev => ({
        ...prev,  // Keep default values
        ...response.data  // Override with server data
      }));
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
    }
  };
  
  const handleSaveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await companySettingsAPI.update(companySettings);
      toast.success('Bedrijfsinstellingen opgeslagen');
    } catch (error) {
      toast.error('Kon instellingen niet opslaan');
      console.error(error);
    } finally {
      setSavingCompany(false);
    }
  };
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Gebruik een PNG, JPG of WEBP bestand');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Bestand is te groot (max 2MB)');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const response = await companySettingsAPI.uploadLogo(file);
      setCompanySettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo geüpload');
    } catch (error) {
      toast.error('Kon logo niet uploaden');
      console.error(error);
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const handleCompanyChange = (field, value) => {
    setCompanySettings(prev => ({ ...prev, [field]: value }));
  };


  const fetchRestoreOptions = async () => {
    try {
      const response = await adminAPI.getRestoreOptions();
      setRestoreOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch restore options:', error);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleRestoreClick = (option) => {
    if (!option.snapshot_available) {
      toast.error('Geen backup beschikbaar voor dit tijdstip');
      return;
    }
    setSelectedRestore(option);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!selectedRestore) return;
    
    setRestoring(true);
    try {
      const response = await adminAPI.restoreByTime(selectedRestore.minutes);
      toast.success(`Data hersteld naar ${formatDateTime(selectedRestore.snapshot_time)}`);
      setRestoreDialogOpen(false);
      fetchRestoreOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Herstel mislukt');
    } finally {
      setRestoring(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight">{t('settingsTitle')}</h1>
        <p className="text-muted-foreground mt-1">{t('settingsDescription')}</p>
      </div>

      {/* Profile Card - Show for all users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {t('profile')}
          </CardTitle>
          <CardDescription>{t('yourAccountDetails')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div>
              <p className="text-lg font-medium">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme Toggle Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            Weergave
          </CardTitle>
          <CardDescription>Kies je voorkeursthema</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button
              variant={theme === 'dark' ? 'default' : 'outline'}
              className={`flex-1 flex items-center justify-center gap-2 h-20 ${theme === 'dark' ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setTheme('dark')}
              data-testid="theme-dark-btn"
            >
              <Moon className="w-5 h-5" />
              <div className="flex flex-col items-start">
                <span className="font-medium">Donker</span>
                <span className="text-xs opacity-70">Dark mode</span>
              </div>
            </Button>
            <Button
              variant={theme === 'light' ? 'default' : 'outline'}
              className={`flex-1 flex items-center justify-center gap-2 h-20 ${theme === 'light' ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setTheme('light')}
              data-testid="theme-light-btn"
            >
              <Sun className="w-5 h-5" />
              <div className="flex flex-col items-start">
                <span className="font-medium">Licht</span>
                <span className="text-xs opacity-70">Light mode</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Language Toggle Card - For all users (cleaners get extra languages) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t('language')}
          </CardTitle>
          <CardDescription>{t('chooseLanguage')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant={language === 'nl' ? 'default' : 'outline'}
              className={`flex items-center justify-center gap-2 h-16 ${language === 'nl' ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => {
                setLanguage('nl');
                toast.success('Taal gewijzigd naar Nederlands');
              }}
              data-testid="language-nl-btn"
            >
              <span className="text-xl">🇳🇱</span>
              <div className="flex flex-col items-start">
                <span className="font-medium text-sm">Nederlands</span>
                <span className="text-xs opacity-70">Dutch</span>
              </div>
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              className={`flex items-center justify-center gap-2 h-16 ${language === 'en' ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => {
                setLanguage('en');
                toast.success('Language changed to English');
              }}
              data-testid="language-en-btn"
            >
              <span className="text-xl">🇬🇧</span>
              <div className="flex flex-col items-start">
                <span className="font-medium text-sm">English</span>
                <span className="text-xs opacity-70">Engels</span>
              </div>
            </Button>
            {/* Extra languages for cleaners only */}
            {user?.role === 'cleaner' && (
              <>
                <Button
                  variant={language === 'pl' ? 'default' : 'outline'}
                  className={`flex items-center justify-center gap-2 h-16 ${language === 'pl' ? 'bg-primary text-primary-foreground' : ''}`}
                  onClick={() => {
                    setLanguage('pl');
                    toast.success('Język zmieniony na polski');
                  }}
                  data-testid="language-pl-btn"
                >
                  <span className="text-xl">🇵🇱</span>
                  <div className="flex flex-col items-start">
                    <span className="font-medium text-sm">Polski</span>
                    <span className="text-xs opacity-70">Polish</span>
                  </div>
                </Button>
                <Button
                  variant={language === 'uk' ? 'default' : 'outline'}
                  className={`flex items-center justify-center gap-2 h-16 ${language === 'uk' ? 'bg-primary text-primary-foreground' : ''}`}
                  onClick={() => {
                    setLanguage('uk');
                    toast.success('Мову змінено на українську');
                  }}
                  data-testid="language-uk-btn"
                >
                  <span className="text-xl">🇺🇦</span>
                  <div className="flex flex-col items-start">
                    <span className="font-medium text-sm">Українська</span>
                    <span className="text-xs opacity-70">Ukrainian</span>
                  </div>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* For cleaners, only show the above sections (Profile, Theme, Language) */}
      {user?.role === 'cleaner' && (
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Selecteer je voorkeurstaal hierboven. De CRM interface zal in die taal worden weergegeven.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Rest of settings - only for non-cleaners */}
      {user?.role !== 'cleaner' && (
        <>
      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Wachtwoord Wijzigen
          </CardTitle>
          <CardDescription>Wijzig je inlogwachtwoord</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Huidig wachtwoord</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                placeholder="Voer je huidige wachtwoord in"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nieuw wachtwoord</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Minimaal 6 tekens"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Bevestig nieuw wachtwoord</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Herhaal je nieuwe wachtwoord"
            />
          </div>
          
          <Button 
            onClick={handleChangePassword} 
            disabled={changingPassword}
            className="w-full sm:w-auto"
          >
            {changingPassword ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Wijzigen...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Wachtwoord Wijzigen
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Restore/Backup Section - Admin Only */}
      {hasRole('admin') && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Herstel & Backup
          </CardTitle>
          <CardDescription>
            Herstel de CRM naar een eerder tijdstip of maak een backup
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Time-based restore */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Herstel naar tijdstip
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              De CRM maakt automatisch elke 10 minuten een backup. Kies een tijdstip om terug te gaan.
            </p>
            
            {restoreLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Laden...
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {restoreOptions.map((option) => (
                  <Button
                    key={option.minutes}
                    variant={option.snapshot_available ? "outline" : "ghost"}
                    size="sm"
                    className={`text-xs ${!option.snapshot_available ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-500/10 hover:text-orange-500 hover:border-orange-500/50'}`}
                    onClick={() => handleRestoreClick(option)}
                    disabled={!option.snapshot_available}
                    data-testid={`restore-btn-${option.minutes}`}
                  >
                    {option.label.replace(' geleden', '')}
                  </Button>
                ))}
              </div>
            )}
            
            <p className="text-xs text-muted-foreground mt-3">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Let op: Herstel vervangt alle huidige data. Er wordt automatisch een backup gemaakt voor herstel.
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Role Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Rol & Rechten
          </CardTitle>
          <CardDescription>Jouw toegangsniveau in het systeem</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 rounded-md bg-primary/20 text-primary font-medium">
              {getRoleLabel(user?.role)}
            </div>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            {user?.role === 'admin' && (
              <p>Als Administrator heb je volledige toegang tot alle functies, inclusief gebruikersbeheer, data verwijdering, backups en systeeminstellingen.</p>
            )}
            {user?.role === 'technician' && (
              <p>Als Technicus kun je installaties beheren, onderhoudsrecords toevoegen en reinigingsactiviteiten plannen.</p>
            )}
            {user?.role === 'sales' && (
              <p>Als Sales kun je klantgegevens bekijken, offertes maken en klantrelaties beheren.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Systeeminformatie
          </CardTitle>
          <CardDescription>Platformdetails</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Platform</span>
              <span>Business Assistant</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Versie</span>
              <span>1.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Auto-backup</span>
              <span className="text-green-500">Dagelijks 03:00 (cloud) + elk uur (intern)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backups - Admin only */}
      {hasRole('admin') && <BackupPanel />}

      {/* Mijn Email Account Card - For ALL Users (Admin and Sales) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Mijn Email Account
          </CardTitle>
          <CardDescription>
            Koppel je persoonlijke email account om mails te versturen via de CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!personalEmailAccount && (
            <div className="flex justify-end">
              <Button onClick={() => { setEmailDialogType('personal'); setEmailDialogOpen(true); }} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Email Account Koppelen
              </Button>
            </div>
          )}

          {emailAccountsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : !personalEmailAccount ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Geen persoonlijk email account gekoppeld</p>
              <p className="text-sm mt-1">
                Voeg je email account toe om via de CRM emails te versturen met jouw eigen adres
              </p>
            </div>
          ) : (
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${personalEmailAccount.is_active ? 'bg-green-500/10' : 'bg-gray-500/10'}`}>
                    <Mail className={`w-5 h-5 ${personalEmailAccount.is_active ? 'text-green-500' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium">{personalEmailAccount.display_name || personalEmailAccount.email_address}</p>
                    <p className="text-sm text-muted-foreground">{personalEmailAccount.email_address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {personalEmailAccount.is_active ? (
                    <Badge variant="outline" className="text-green-500 border-green-500">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Verbonden
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-500">
                      Inactief
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestEmailAccount(personalEmailAccount.id)}
                  disabled={testingEmailAccount === personalEmailAccount.id}
                >
                  {testingEmailAccount === personalEmailAccount.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )}
                  <span className="ml-1">Test</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUpdatePasswordAccountId(personalEmailAccount.id)}
                  title="Wachtwoord wijzigen"
                >
                  <KeyRound className="w-4 h-4" />
                  <span className="ml-1">Wachtwoord</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                  onClick={() => setDeleteEmailAccountId(personalEmailAccount.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Settings - Only for Admin */}
      {hasRole('admin') && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Bedrijfsgegevens
              </CardTitle>
              <CardDescription>Deze gegevens worden gebruikt op offertes en documenten</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo */}
              <div className="space-y-3">
                <Label>Bedrijfslogo</Label>
                <div className="flex items-center gap-4">
                  {companySettings.logo_url ? (
                    <div className="w-24 h-24 rounded-lg border bg-white flex items-center justify-center p-2">
                      <img 
                        src={companySettings.logo_url.startsWith('http') ? companySettings.logo_url : `${API_URL}${companySettings.logo_url}`} 
                        alt="Logo" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('logo-upload').click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploaden...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" />Logo uploaden</>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground">PNG, JPG of WEBP. Max 2MB.</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bedrijfsnaam</Label>
                  <Input
                    value={companySettings.company_name}
                    onChange={(e) => handleCompanyChange('company_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>BTW-nummer</Label>
                  <Input
                    value={companySettings.vat_number}
                    onChange={(e) => handleCompanyChange('vat_number', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Adres (volledig voor referentie)</Label>
                <Input
                  value={companySettings.address}
                  onChange={(e) => handleCompanyChange('address', e.target.value)}
                  placeholder="Trawoollaan 1B, B-1830 Machelen"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Adresregel 1 (straat + nr)</Label>
                  <Input
                    value={companySettings.address_line1}
                    onChange={(e) => handleCompanyChange('address_line1', e.target.value)}
                    placeholder="Trawoollaan 1B"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresregel 2 (postcode + stad)</Label>
                  <Input
                    value={companySettings.address_line2}
                    onChange={(e) => handleCompanyChange('address_line2', e.target.value)}
                    placeholder="B-1830 Machelen"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={companySettings.email}
                    onChange={(e) => handleCompanyChange('email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefoon</Label>
                  <Input
                    value={companySettings.phone}
                    onChange={(e) => handleCompanyChange('phone', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Offerte Standaarden
              </CardTitle>
              <CardDescription>Standaard waarden voor nieuwe offertes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Standaard BTW %</Label>
                  <Input
                    type="number"
                    value={companySettings.default_vat_percentage}
                    onChange={(e) => handleCompanyChange('default_vat_percentage', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Geldigheid offerte (dagen)</Label>
                  <Input
                    type="number"
                    value={companySettings.default_validity_days}
                    onChange={(e) => handleCompanyChange('default_validity_days', parseInt(e.target.value) || 14)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prijs per paneel (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={companySettings.default_cleaning_price_per_panel}
                    onChange={(e) => handleCompanyChange('default_cleaning_price_per_panel', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Verplaatsingskosten forfait (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={companySettings.default_travel_costs}
                  onChange={(e) => handleCompanyChange('default_travel_costs', parseFloat(e.target.value) || 0)}
                  className="max-w-xs"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Standaard betalingsvoorwaarden</Label>
                <Textarea
                  value={companySettings.default_payment_terms}
                  onChange={(e) => handleCompanyChange('default_payment_terms', e.target.value)}
                  rows={3}
                  placeholder="100% 3-Jaarlijks te factureren bij aanvang van deze overeenkomst..."
                />
              </div>
              
              <div className="space-y-2">
                <Label>Inbegrepen in de offerte</Label>
                <Textarea
                  value={companySettings.included_in_offerte}
                  onChange={(e) => handleCompanyChange('included_in_offerte', e.target.value)}
                  rows={3}
                  placeholder="Reinigen -en behandelen PV panelen&#10;Nazicht volledige PV installatie"
                />
                <p className="text-xs text-muted-foreground">Elke regel op een nieuwe lijn</p>
              </div>
              
              {/* Email Signature */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Handtekening
                </Label>
                <HtmlEditor
                  value={companySettings.email_signature || ''}
                  onChange={(html) => handleCompanyChange('email_signature', html)}
                  placeholder="Met vriendelijke groet, Solar Engineering BV..."
                />
                <p className="text-xs text-muted-foreground">Deze handtekening wordt automatisch toegevoegd aan alle uitgaande emails</p>
              </div>
              
              {/* Installation Payment Terms */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Betalingstermijnen Installaties
                </h4>
                <p className="text-xs text-muted-foreground">Standaard percentages voor facturen bij installatie-offertes. Totaal moet 100% zijn.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Termijn 1 */}
                  <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                    <Label className="text-xs font-medium">Termijn 1 - Voorschot</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={companySettings.installation_term1_percentage}
                        onChange={(e) => handleCompanyChange('installation_term1_percentage', parseFloat(e.target.value) || 0)}
                        className="w-20"
                      />
                      <span className="text-sm">%</span>
                    </div>
                    <Input
                      value={companySettings.installation_term1_description}
                      onChange={(e) => handleCompanyChange('installation_term1_description', e.target.value)}
                      placeholder="Voorschot bij bestelling"
                      className="text-sm"
                    />
                  </div>
                  
                  {/* Termijn 2 */}
                  <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                    <Label className="text-xs font-medium">Termijn 2 - Na montage</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={companySettings.installation_term2_percentage}
                        onChange={(e) => handleCompanyChange('installation_term2_percentage', parseFloat(e.target.value) || 0)}
                        className="w-20"
                      />
                      <span className="text-sm">%</span>
                    </div>
                    <Input
                      value={companySettings.installation_term2_description}
                      onChange={(e) => handleCompanyChange('installation_term2_description', e.target.value)}
                      placeholder="Na montage en inwerkingstelling"
                      className="text-sm"
                    />
                  </div>
                  
                  {/* Termijn 3 */}
                  <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                    <Label className="text-xs font-medium">Termijn 3 - Eindfactuur</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={companySettings.installation_term3_percentage}
                        onChange={(e) => handleCompanyChange('installation_term3_percentage', parseFloat(e.target.value) || 0)}
                        className="w-20"
                      />
                      <span className="text-sm">%</span>
                    </div>
                    <Input
                      value={companySettings.installation_term3_description}
                      onChange={(e) => handleCompanyChange('installation_term3_description', e.target.value)}
                      placeholder="Na AREI-keuring"
                      className="text-sm"
                    />
                  </div>
                </div>
                
                {/* Total percentage indicator */}
                {(() => {
                  const total = (companySettings.installation_term1_percentage || 0) + 
                               (companySettings.installation_term2_percentage || 0) + 
                               (companySettings.installation_term3_percentage || 0);
                  const isValid = Math.abs(total - 100) < 0.01;
                  return (
                    <div className={`text-sm ${isValid ? 'text-green-600' : 'text-red-500'} flex items-center gap-2`}>
                      {isValid ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <AlertTriangle className="w-4 h-4" />
                      )}
                      Totaal: {total}% {isValid ? '(correct)' : '(moet 100% zijn)'}
                    </div>
                  );
                })()}
              </div>
              
              <div className="space-y-2">
                <Label>Offerte nummer prefix (Reiniging)</Label>
                <Input
                  value={companySettings.offerte_number_prefix}
                  onChange={(e) => handleCompanyChange('offerte_number_prefix', e.target.value)}
                  placeholder="2026-"
                  className="max-w-xs"
                />
                <p className="text-xs text-muted-foreground">Prefix voor reinigingsoffertes (bijv. "2026-" geeft "2026-0001")</p>
              </div>

              <div className="space-y-2">
                <Label>Offerte nummer prefix (Installatie)</Label>
                <Input
                  value={companySettings.installation_offerte_prefix}
                  onChange={(e) => handleCompanyChange('installation_offerte_prefix', e.target.value)}
                  placeholder="INST-2026-"
                  className="max-w-xs"
                />
                <p className="text-xs text-muted-foreground">Prefix voor installatie offertes (bijv. "INST-2026-" geeft "INST-2026-0001")</p>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={handleSaveCompanySettings} disabled={savingCompany}>
                  {savingCompany ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opslaan...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Instellingen Opslaan</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Templates Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Offerte Templates
              </CardTitle>
              <CardDescription>Beheer je installatie offerte templates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={openNewTemplate} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe Template
                </Button>
              </div>
              
              {templatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Geen templates gevonden</p>
                  <p className="text-sm mt-1">Maak templates aan vanuit Offertes of klik op "Nieuwe Template"</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div key={template.id} className="p-4 border rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-medium">{template.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {template.items?.length || 0} items • BTW {template.vat_percentage}%
                        </p>
                        {template.description && (
                          <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openEditTemplate(template)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => setDeleteTemplateId(template.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company Email Accounts Card - Admin Only */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Bedrijfs Email Accounts
              </CardTitle>
              <CardDescription>
                Koppel bedrijfsemail accounts (bv. info@) om klantcommunicatie automatisch te synchroniseren
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => { setEmailDialogType('company'); setEmailDialogOpen(true); }} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Bedrijfs Email Toevoegen
                </Button>
              </div>

              {emailAccountsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : companyEmailAccounts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Geen bedrijfs email accounts gekoppeld</p>
                  <p className="text-sm mt-1">
                    Voeg een bedrijfs email account toe (bv. info@) om klantcommunicatie te synchroniseren
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {companyEmailAccounts.map((account) => (
                    <div key={account.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${account.is_active ? 'bg-green-500/10' : 'bg-gray-500/10'}`}>
                            <Building2 className={`w-5 h-5 ${account.is_active ? 'text-green-500' : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{account.display_name || account.email_address}</p>
                            <p className="text-sm text-muted-foreground">{account.email_address}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">Bedrijf</Badge>
                          {account.is_active ? (
                            <Badge variant="outline" className="text-green-500 border-green-500">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Verbonden
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">
                              Inactief
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                          {account.last_sync ? (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Laatst gesynchroniseerd: {new Date(account.last_sync).toLocaleString('nl-NL')}
                            </span>
                          ) : (
                            <span>Nog niet gesynchroniseerd</span>
                          )}
                          {account.total_emails_synced > 0 && (
                            <span className="ml-3">• {account.total_emails_synced} emails</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestEmailAccount(account.id)}
                            disabled={testingEmailAccount === account.id}
                          >
                            {testingEmailAccount === account.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Link2 className="w-4 h-4" />
                            )}
                            <span className="ml-1">Test</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncEmailAccount(account.id)}
                            disabled={syncingEmailAccount === account.id}
                          >
                            {syncingEmailAccount === account.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                            <span className="ml-1">Sync Nu</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setUpdatePasswordAccountId(account.id)}
                            title="Wachtwoord wijzigen"
                          >
                            <KeyRound className="w-4 h-4" />
                            <span className="ml-1">Wachtwoord</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => setDeleteEmailAccountId(account.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Hoe werkt het?</strong> Bedrijfs emails worden automatisch elke 5 minuten gesynchroniseerd 
                  en gekoppeld aan klanten op basis van hun email adres. Wanneer je emails verstuurt via de CRM, 
                  worden deze verstuurd via jouw <strong>persoonlijk account</strong>. Als je geen persoonlijk account hebt, 
                  wordt het bedrijfs email account gebruikt als fallback.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Email Templates Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Email Templates
              </CardTitle>
              <CardDescription>
                Beheer standaard email templates voor offertes, facturen en afspraken
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-end">
                <Button 
                  onClick={() => {
                    setEditingEmailTemplate(null);
                    setEmailTemplateForm({
                      template_type: 'reiniging_offerte',
                      name: '',
                      subject: '',
                      body_html: '',
                      is_active: true,
                      cc_emails: '',
                      cc_creator: false
                    });
                    setEmailTemplateDialogOpen(true);
                  }} 
                  size="sm"
                  data-testid="add-email-template-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe Template
                </Button>
              </div>

              {emailTemplatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : emailTemplates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Geen email templates gevonden</p>
                  <p className="text-sm mt-1">
                    Templates worden automatisch aangemaakt bij eerste gebruik
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailTemplates.map((template) => (
                    <div key={template.id} className="p-4 border rounded-lg" data-testid={`email-template-${template.template_type}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${template.is_active ? 'bg-green-500/10' : 'bg-gray-500/10'}`}>
                            <Mail className={`w-5 h-5 ${template.is_active ? 'text-green-500' : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{template.name}</p>
                            <p className="text-sm text-muted-foreground">{template.subject}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={template.is_active ? "default" : "secondary"}>
                            {template.is_active ? 'Actief' : 'Inactief'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {template.template_type.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditEmailTemplate(template)}
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Bewerken
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => setDeleteEmailTemplateId(template.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Beschikbare variabelen:</strong> {"{{voornaam}}"}, {"{{naam}}"}, {"{{klant_naam}}"}, {"{{adres}}"}, {"{{offerte_nummer}}"}, 
                  {"{{factuur_nummer}}"}, {"{{totaal_bedrag}}"}, {"{{aantal_panelen}}"}, {"{{producten_lijst}}"}, 
                  {"{{geldigheid_dagen}}"}, {"{{betalingstermijn}}"}, {"{{bedrijf_naam}}"}, {"{{datum}}"}, {"{{tijd}}"}, {"{{klantportaal_link}}"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  💡 Gebruik {"{{voornaam}}"} voor voornaam en {"{{naam}}"} voor achternaam apart
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Email Account Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {emailDialogType === 'personal' ? <Mail className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
              {emailDialogType === 'personal' ? 'Persoonlijk Email Account Koppelen' : 'Bedrijfs Email Account Toevoegen'}
            </DialogTitle>
            <DialogDescription>
              {emailDialogType === 'personal' 
                ? 'Koppel je persoonlijke email account om vanuit de CRM emails te versturen met jouw eigen adres'
                : 'Voeg een bedrijfs email account toe (bv. info@) om klantcommunicatie te synchroniseren'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email_address">Email Adres *</Label>
              <Input
                id="email_address"
                type="email"
                placeholder={emailDialogType === 'personal' ? 'jouw.naam@bedrijf.be' : 'info@bedrijf.be'}
                value={emailForm.email_address}
                onChange={(e) => setEmailForm({ ...emailForm, email_address: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="imap_password">IMAP Wachtwoord *</Label>
              <Input
                id="imap_password"
                type="password"
                placeholder="Je email wachtwoord"
                value={emailForm.imap_password}
                onChange={(e) => setEmailForm({ ...emailForm, imap_password: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Dit is je normale email wachtwoord voor one.com
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="display_name">Weergavenaam (optioneel)</Label>
              <Input
                id="display_name"
                placeholder={emailDialogType === 'personal' ? 'bijv. Charli Van Dyck' : 'bijv. Info Mailbox'}
                value={emailForm.display_name}
                onChange={(e) => setEmailForm({ ...emailForm, display_name: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleAddEmailAccount} disabled={savingEmailAccount}>
              {savingEmailAccount ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verbinden...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Toevoegen & Testen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Email Account Confirmation */}
      <AlertDialog open={!!deleteEmailAccountId} onOpenChange={() => setDeleteEmailAccountId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Email Account Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je dit email account wilt verwijderen? 
              De gesynchroniseerde emails blijven bewaard bij de klanten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEmailAccount}
              className="bg-red-500 hover:bg-red-600"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Update Email Password Dialog */}
      <Dialog open={!!updatePasswordAccountId} onOpenChange={() => { setUpdatePasswordAccountId(null); setNewPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Wachtwoord Wijzigen
            </DialogTitle>
            <DialogDescription>
              Voer het nieuwe wachtwoord in voor het email account. Dit is nodig als de encryptiesleutel is gewijzigd.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">Nieuw Wachtwoord *</Label>
              <Input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Voer nieuw wachtwoord in"
                autoComplete="new-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUpdatePasswordAccountId(null); setNewPassword(''); }}>
              Annuleren
            </Button>
            <Button onClick={handleUpdatePassword} disabled={!newPassword}>
              <Save className="w-4 h-4 mr-2" />
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Data Herstellen
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Je staat op het punt de CRM te herstellen naar <strong>{selectedRestore?.label}</strong>.
              </p>
              {selectedRestore?.snapshot_time && (
                <p className="text-sm">
                  Backup van: {formatDateTime(selectedRestore.snapshot_time)}
                </p>
              )}
              {selectedRestore?.snapshot_stats && (
                <div className="bg-muted p-3 rounded text-sm">
                  <p className="font-medium mb-1">Data in deze backup:</p>
                  <ul className="space-y-1">
                    <li>• {selectedRestore.snapshot_stats.customers} klanten</li>
                    <li>• {selectedRestore.snapshot_stats.properties} adressen</li>
                    <li>• {selectedRestore.snapshot_stats.installations} installaties</li>
                  </ul>
                </div>
              )}
              <p className="text-orange-600 font-medium">
                Let op: Alle huidige wijzigingen na dit tijdstip gaan verloren. Er wordt automatisch een backup gemaakt voordat de herstel plaatsvindt.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRestoreConfirm}
              disabled={restoring}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Herstellen...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Ja, herstel data
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Edit Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Template Bewerken' : 'Nieuwe Template'}
            </DialogTitle>
            <DialogDescription>
              Templates kunnen worden gebruikt bij het aanmaken van installatie offertes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Naam *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Bijv. Standaard Zonnepanelen"
                />
              </div>
              <div className="space-y-2">
                <Label>BTW Percentage</Label>
                <Input
                  type="number"
                  value={templateForm.vat_percentage}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, vat_percentage: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Input
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Korte beschrijving van de template"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Betalingsvoorwaarden</Label>
              <Textarea
                value={templateForm.payment_terms}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, payment_terms: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Inbegrepen in offerte</Label>
              <Textarea
                value={templateForm.included_in_offerte}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, included_in_offerte: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={templateForm.notes}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Items section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Items ({templateForm.items?.length || 0})
              </Label>
              {templateForm.items?.length > 0 ? (
                <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                  {templateForm.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm border-b pb-2 last:border-0 last:pb-0">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.quantity}x • €{item.unit_price?.toFixed(2)}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 text-red-500"
                        onClick={() => setTemplateForm(prev => ({
                          ...prev,
                          items: prev.items.filter((_, i) => i !== index)
                        }))}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground p-3 border rounded-lg">
                  Geen items toegevoegd. Items kunnen worden toegevoegd via "Opslaan als Template" in Offertes.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
              {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingTemplate ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Dialog */}
      <AlertDialog open={!!deleteTemplateId} onOpenChange={() => setDeleteTemplateId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Template Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze template wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTemplate} className="bg-red-600 hover:bg-red-500">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Template Dialog with Live Preview */}
      <Dialog open={emailTemplateDialogOpen} onOpenChange={setEmailTemplateDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {editingEmailTemplate ? 'Email Template Bewerken' : 'Nieuwe Email Template'}
            </DialogTitle>
            <DialogDescription>
              Maak of bewerk een email template - preview toont hoe de email er uit zal zien
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Left side - Editor */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template_type">Type *</Label>
                  <select
                    id="template_type"
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    value={emailTemplateForm.template_type}
                    onChange={(e) => setEmailTemplateForm(prev => ({ ...prev, template_type: e.target.value }))}
                  >
                    <option value="reiniging_offerte">Reiniging Offerte</option>
                    <option value="reiniging_offerte_bedankt">Reiniging Offerte - Bedanktmail</option>
                    <option value="reiniging_afspraak">Reiniging Afspraak</option>
                    <option value="reiniging_factuur">Reiniging Factuur</option>
                    <option value="installatie_offerte">Installatie Offerte</option>
                    <option value="installatie_offerte_bedankt">Installatie Offerte - Bedanktmail</option>
                    <option value="installatie_afspraak">Installatie Afspraak</option>
                    <option value="sales_afspraak_bevestiging">Sales Afspraak Bevestiging</option>
                    <option value="installatie_factuur">Installatie Factuur (Algemeen)</option>
                    <option value="installatie_factuur_voorschot">Installatie Factuur - Voorschot</option>
                    <option value="installatie_factuur_montage">Installatie Factuur - Na Montage</option>
                    <option value="installatie_factuur_eindfactuur">Installatie Factuur - Eindfactuur</option>
                    <option value="algemeen">Algemene Email</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template_name">Naam *</Label>
                  <Input
                    id="template_name"
                    placeholder="Bijv. Reiniging Offerte Email"
                    value={emailTemplateForm.name}
                    onChange={(e) => setEmailTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template_subject">Onderwerp *</Label>
                <Input
                  id="template_subject"
                  placeholder="Bijv. Offerte reiniging - {{offerte_nummer}}"
                  value={emailTemplateForm.subject}
                  onChange={(e) => setEmailTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template_cc">Standaard in CC</Label>
                <Input
                  id="template_cc"
                  placeholder="Bijv. info@solar-engineering.be, admin@solar-engineering.be"
                  value={emailTemplateForm.cc_emails}
                  onChange={(e) => setEmailTemplateForm(prev => ({ ...prev, cc_emails: e.target.value }))}
                  data-testid="template-cc-input"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="template_cc_user"
                    checked={emailTemplateForm.cc_creator}
                    onChange={(e) => setEmailTemplateForm(prev => ({ ...prev, cc_creator: e.target.checked }))}
                    data-testid="template-cc-user-checkbox"
                  />
                  <Label htmlFor="template_cc_user" className="font-normal cursor-pointer">Opmaker van de offerte/factuur automatisch in CC</Label>
                </div>
                <p className="text-xs text-muted-foreground">Meerdere adressen scheiden met een komma. Deze CC's worden automatisch toegevoegd bij het verzenden van mails met deze template.</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template_body">Email Inhoud *</Label>
                <HtmlEditor
                  value={emailTemplateForm.body_html}
                  onChange={(html) => setEmailTemplateForm(prev => ({ ...prev, body_html: html }))}
                  placeholder="Beste {{voornaam}}, Bijgevoegd vindt u onze offerte..."
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Variabelen: {"{{voornaam}}"}, {"{{naam}}"}, {"{{klant_naam}}"}, {"{{adres}}"}, {"{{datum}}"}, {"{{tijd}}"}, {"{{offerte_nummer}}"}, {"{{totaal_bedrag}}"}, {"{{bedrijf_naam}}"}, {"{{bedrijf_website}}"}, {"{{bedrijf_email}}"}, {"{{bedrijf_telefoon}}"}, {"{{klantportaal_link}}"}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="template_active"
                  checked={emailTemplateForm.is_active}
                  onChange={(e) => setEmailTemplateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="template_active" className="text-sm font-normal">
                  Template actief
                </Label>
              </div>
            </div>
            
            {/* Right side - Live Preview */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Live Preview
              </Label>
              <EmailPreview
                subject={emailTemplateForm.subject
                  .replace(/\{\{voornaam\}\}/g, 'Marnix')
                  .replace(/\{\{naam\}\}/g, 'Janssen')
                  .replace(/\{\{klant_naam\}\}/g, 'Marnix Janssen')
                  .replace(/\{\{offerte_nummer\}\}/g, 'OFF-2026-0001')
                  .replace(/\{\{factuur_nummer\}\}/g, 'FAC-2026-0001')
                  .replace(/\{\{klantportaal_link\}\}/g, '<a href="#" style="color:#2563eb;text-decoration:underline;font-weight:600;">Klant Portaal voor Marnix Janssen</a>')
                  .replace(/\{\{datum\}\}/g, new Date().toLocaleDateString('nl-BE'))
                  .replace(/\{\{tijd\}\}/g, '14:00')
                  .replace(/\{\{gebruiker\}\}/g, user ? `${user.first_name} ${user.last_name}`.trim() : 'Medewerker')}
                bodyHtml={emailTemplateForm.body_html
                  .replace(/\{\{voornaam\}\}/g, 'Marnix')
                  .replace(/\{\{naam\}\}/g, 'Janssen')
                  .replace(/\{\{klant_naam\}\}/g, 'Marnix')
                  .replace(/\{\{adres\}\}/g, 'Kerkstraat 123, 1000 Brussel')
                  .replace(/\{\{datum\}\}/g, new Date().toLocaleDateString('nl-BE'))
                  .replace(/\{\{tijd\}\}/g, '14:00')
                  .replace(/\{\{dag\}\}/g, 'maandag')
                  .replace(/\{\{offerte_nummer\}\}/g, 'OFF-2026-0001')
                  .replace(/\{\{factuur_nummer\}\}/g, 'FAC-2026-0001')
                  .replace(/\{\{klantportaal_link\}\}/g, '<a href="#" style="color:#2563eb;text-decoration:underline;font-weight:600;">Klant Portaal voor Marnix Janssen</a>')
                  .replace(/\{\{totaal_bedrag\}\}/g, '1.250,00')
                  .replace(/\{\{betalingstermijn\}\}/g, '14')
                  .replace(/\{\{bedrijf_naam\}\}/g, 'Solar Engineering')
                  .replace(/\{\{bedrijf_website\}\}/g, 'www.solar-engineering.be')
                  .replace(/\{\{bedrijf_email\}\}/g, 'info@solar-engineering.be')
                  .replace(/\{\{bedrijf_telefoon\}\}/g, '+32 467 099 099')
                  .replace(/\{\{producten_lijst\}\}/g, '<ul><li>Product A - €500</li><li>Product B - €750</li></ul>')
                  .replace(/\{\{gebruiker\}\}/g, user ? `${user.first_name} ${user.last_name}`.trim() : 'Medewerker')}
                toEmail="jan.janssen@voorbeeld.be"
                toName="Jan Janssen"
                className="h-full"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailTemplateDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveEmailTemplate} disabled={savingEmailTemplate}>
              {savingEmailTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingEmailTemplate ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Email Template Dialog */}
      <AlertDialog open={!!deleteEmailTemplateId} onOpenChange={() => setDeleteEmailTemplateId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Email Template Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze email template wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEmailTemplate} className="bg-red-600 hover:bg-red-500">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
      )}
    </div>
  );
};

export default Settings;
